import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { Button } from '../ui/Button';
import { localToday } from '../../utils/date';
import { expandTemplate, applyTemplateEntries } from '../../utils/templateApply';

// Logging templates (SS0) — the one-tap, adjustable log bundle.
//
// A template is a saved set of would-be entries ("morning stack", "leg day").
// Logging it EXPANDS its items into editable rows: uncheck what you skipped,
// tweak a value, pick the date, and the survivors POST straight to the existing
// /api/health/entries (source="template"). No new write path — the same proven
// bulk endpoint the conversational logger uses. This is the friction-killer:
// give us your data in one tap instead of retyping the same stack every day.
//
// Deliberately NOT under components/supplements: LogTemplate is domain-agnostic and
// a workout routine is a first-class use of it. The create form asks what kind of
// routine you're building before anything else, rather than defaulting every item to
// a supplement and making everyone else correct it row by row.

const inputCls =
  'flex h-9 w-full rounded-lg border border-line bg-white px-3 py-1.5 text-sm ' +
  'placeholder:text-fg-3 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-fg-3 focus-visible:ring-offset-2  ' +
  '   ' +
  'dark:placeholder:text-fg-3 ';

const labelCls = 'text-sm font-medium text-fg-2';

// What kind of routine you're saving. This drives the default category for its items
// (the whole point — a workout shouldn't start life as a supplement), the icon, and
// the template's `domain` hint. Workout leads because it's the case the old
// supplement-defaulted form served worst. Nothing here is a restriction: the per-item
// category dropdown still offers everything, so a routine can mix domains.
const ROUTINE_KINDS = [
  {
    key: 'exercise', label: 'Workout', icon: '🏋️', slug: 'exercise.strength',
    placeholder: 'exercise (e.g. bench press)', namePlaceholder: 'Workout A',
    duration: true,
  },
  {
    key: 'supplements', label: 'Supplements', icon: '💊', slug: 'nutrition.supplement',
    placeholder: 'supplement (e.g. vitamin d3)', namePlaceholder: 'Morning stack',
  },
  {
    key: 'food', label: 'Meal', icon: '🍽️', slug: 'nutrition.food',
    placeholder: 'food (e.g. oatmeal)', namePlaceholder: 'Usual breakfast',
  },
  {
    key: 'other', label: 'Other', icon: '✳️', slug: 'other',
    placeholder: 'what you log', namePlaceholder: 'Evening routine',
  },
];

const itemLabel = (it) =>
  (it.entry_type || 'item').replace(/_/g, ' ');

// `refreshKey` / `onChanged` keep this in step with the checklist editor above it: they
// each own half of the same state (the editor pins routines, this manages them and stars
// them), so a change in one must reload the other or you get a stale "No saved routines
// yet" sitting directly above the routine you just created.
const RoutineManager = ({ defaultDate, onEntrySaved, refreshKey = 0, onChanged }) => {
  const today = useMemo(() => localToday(), []);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  // Apply state
  const [applyingId, setApplyingId] = useState(null);
  const [rows, setRows] = useState([]);          // {…item, include, value}
  const [applyDate, setApplyDate] = useState(defaultDate || today);
  const [busy, setBusy] = useState(false);

  // Create state. `newKind` is null until the user picks what they're building — the
  // items section stays hidden until then, so no item ever inherits a wrong default.
  const [creating, setCreating] = useState(false);
  const [newKind, setNewKind] = useState(null);
  const [newName, setNewName] = useState('');
  const [newItems, setNewItems] = useState([]);

  const kind = ROUTINE_KINDS.find((k) => k.key === newKind) || null;

  function blankItem(k) {
    return {
      category_slug: (k || kind)?.slug || 'other',
      entry_type: '', value: '', value_unit: '', duration_minutes: '',
    };
  }

  // Switching kind restarts the items with the new default category. Safe because the
  // items section only appears after a kind is chosen, so there's nothing to lose yet.
  const chooseKind = (k) => {
    setNewKind(k.key);
    setNewItems([blankItem(k)]);
    setNote('');
  };

  // Which templates are pinned to the daily checklist. We hold the full checklist
  // item list so pin/unpin can PUT it back intact.
  const [checklistItems, setChecklistItems] = useState(null);
  const pinnedIds = new Set(
    (checklistItems || []).filter((i) => i.kind === 'template').map((i) => i.template_id),
  );

  // Miner-drafted routine offers. Reading the list is also what tops it up
  // server-side, so this section is always as fresh as the last visit.
  const [suggestions, setSuggestions] = useState([]);

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get('/api/templates');
      setTemplates(res.data || []);
    } catch (e) {
      setError('Could not load your templates.');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = () =>
    apiClient
      .get('/api/templates/suggestions')
      .then((res) => setSuggestions(res.data || []))
      .catch(() => {});

  useEffect(() => {
    loadTemplates();
    loadSuggestions();
    apiClient
      .get('/api/checklist/config')
      .then((res) => setChecklistItems(res.data?.items || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);   // re-read when the checklist editor above changes the pins

  const acceptSuggestion = async (s) => {
    try {
      await apiClient.post(`/api/templates/suggestions/${s.id}/accept`);
      toast.success(`“${s.name}” saved — log it by name, or star it for one-tap.`);
      loadTemplates();
      loadSuggestions();
    } catch {
      toast.error('Could not save that routine.');
    }
  };

  const dismissSuggestion = async (s) => {
    try {
      await apiClient.post(`/api/templates/suggestions/${s.id}/dismiss`);
      setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
    } catch {
      toast.error('Could not dismiss that.');
    }
  };

  useEffect(() => {
    apiClient
      .get('/api/health/categories')
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  const togglePin = async (t) => {
    if (!checklistItems) return;
    const isPinned = pinnedIds.has(t.id);
    const next = isPinned
      ? checklistItems.filter((i) => !(i.kind === 'template' && i.template_id === t.id))
      : [...checklistItems, {
          id: `template_${t.id}`, label: t.name, kind: 'template',
          template_id: t.id, meta: '', auto: false, enabled: true,
        }];
    const prev = checklistItems;
    setChecklistItems(next);   // optimistic
    try {
      const res = await apiClient.put('/api/checklist/config', { items: next });
      setChecklistItems(res.data?.items || next);
      toast.success(isPinned
        ? `Unpinned “${t.name}” from today`
        : `Pinned “${t.name}” to today’s checklist`);
      if (onChanged) onChanged();
    } catch {
      setChecklistItems(prev);
      toast.error('Could not update your checklist.');
    }
  };

  // --- Apply -----------------------------------------------------------------
  // A {ref:"regimen"} item is a live placeholder: at apply time we expand it into
  // one editable row per active supplement in the current stack (SS2), so the
  // template always reflects today's stack, not a frozen copy.
  const openApply = async (t) => {
    setNote('');
    setApplyingId(t.id);
    setApplyDate(defaultDate || today);
    setRows(await expandTemplate(t));
  };

  const toggleRow = (i) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, include: !r.include } : r)));

  const editRowValue = (i, v) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, value: v } : r)));

  const submitApply = async (t) => {
    const chosen = rows.filter((r) => r.include);
    if (chosen.length === 0) {
      setNote('Nothing checked — pick at least one item.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { count } = await applyTemplateEntries(t, rows, applyDate);
      setApplyingId(null);
      setNote(`Logged ${count} ${count === 1 ? 'item' : 'items'} from “${t.name}”.`);
      if (onEntrySaved) onEntrySaved();
      loadTemplates();
    } catch (e) {
      setError('Could not log those entries. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // --- Create ----------------------------------------------------------------
  const setNewItem = (i, patch) =>
    setNewItems((its) => its.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const addNewItem = () => setNewItems((its) => [...its, blankItem()]);
  const addRegimenRef = () =>
    setNewItems((its) => [...its, { ref: 'regimen', kind: 'supplement' }]);
  const removeNewItem = (i) => setNewItems((its) => its.filter((_, idx) => idx !== i));

  const resetCreate = () => {
    setCreating(false);
    setNewKind(null);
    setNewName('');
    setNewItems([]);
  };

  const submitCreate = async () => {
    const items = newItems
      .map((it) => {
        if (it.ref === 'regimen') return { ref: 'regimen', kind: it.kind || 'supplement' };
        if (it.category_slug && it.entry_type.trim()) {
          return {
            category_slug: it.category_slug,
            entry_type: it.entry_type.trim(),
            value: it.value === '' ? null : Number(it.value),
            value_unit: it.value_unit.trim() || null,
            duration_minutes:
              it.duration_minutes === '' || it.duration_minutes == null
                ? null
                : Number(it.duration_minutes),
          };
        }
        return null;
      })
      .filter(Boolean);
    if (!newName.trim() || items.length === 0) {
      setNote('A routine needs a name and at least one filled-in item.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/templates', {
        name: newName.trim(),
        items,
        // Carry the kind through so the row is self-describing (and the checklist pin
        // and voice menu get a sensible icon rather than a supplement default).
        domain: kind?.key || null,
        icon: kind?.icon || null,
      });
      resetCreate();
      setNote('Routine saved. Say its name while logging, or star it for one-tap.');
      loadTemplates();
      if (onChanged) onChanged();
    } catch (e) {
      setError('Could not save the template.');
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the “${t.name}” routine? Your logged entries stay.`)) return;
    try {
      await apiClient.delete(`/api/templates/${t.id}`);
      loadTemplates();
      if (onChanged) onChanged();   // the pin above it (if any) is now a dead row
    } catch (e) {
      setError('Could not delete that template.');
    }
  };

  // --- Render ----------------------------------------------------------------
  return (
    <div className="rounded-xl border border-line bg-raised   p-5 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-fg">Your routines</h2>
          <p className="text-sm text-fg-2 ">
            Save a routine once — a workout, a supplement stack, a usual meal — then log it
            by saying its name while logging. Star one (★) to pin it to your daily checklist
            for one-tap logging.
          </p>
        </div>
        {!creating && (
          <Button
            size="sm"
            variant="secondary"
            className="w-full sm:w-auto shrink-0 whitespace-nowrap"
            onClick={() => { setCreating(true); setNote(''); }}
          >
            + New routine
          </Button>
        )}
      </div>

      {error && (
        <p className="text-sm text-off-strong dark:text-off">{error}</p>
      )}
      {note && (
        <p className="text-sm text-good-strong dark:text-good">{note}</p>
      )}

      {/* Create panel */}
      {creating && (
        <div className="rounded-lg border border-line p-4 space-y-3">
          <div className="space-y-1.5">
            <span className={labelCls}>What kind of routine?</span>
            <div className="flex flex-wrap gap-2">
              {ROUTINE_KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => chooseKind(k)}
                  aria-pressed={newKind === k.key}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    newKind === k.key
                      ? 'border-fg-3  text-fg dark:0/15 '
                      : 'border-line text-fg-2 hover:bg-elev   '
                  }`}
                >
                  <span aria-hidden>{k.icon}</span> {k.label}
                </button>
              ))}
            </div>
          </div>

          {kind && (
          <>
          <div className="space-y-1.5">
            <label className={labelCls}>Name</label>
            <input
              className={inputCls}
              placeholder={kind.namePlaceholder}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <p className="text-sm text-fg-2 ">
              You can log it by saying this name out loud — “I did {newName.trim() || kind.namePlaceholder}”.
            </p>
          </div>
          <div className="space-y-2">
            <span className={labelCls}>Items</span>
            {newItems.map((it, i) => (
              it.ref === 'regimen' ? (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 rounded-lg border border-fg-3  dark:border-fg-3/40 dark: px-3 py-2"
                >
                  <span className="text-sm font-medium text-fg ">
                    🧬 My active supplement stack
                    <span className="font-normal text-fg-3 ">
                      {' '}· expands to today’s stack when logged
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeNewItem(i)}
                    className="text-sm text-fg-2 hover:text-off-strong px-1"
                    aria-label="Remove supplement stack reference"
                  >
                    ✕
                  </button>
                </div>
              ) : (
              // Two fixed rows instead of flex-wrap: on a phone the wrap put every field
              // on its own line (five per item). Now it's [what + remove] then the
              // numbers sharing one line, so an item is two lines at any width.
              <div
                key={i}
                className="space-y-2 rounded-lg border border-line p-2"
              >
                <div className="flex items-center gap-2">
                  <input
                    className={`${inputCls} min-w-0 flex-1`}
                    placeholder={kind.placeholder}
                    value={it.entry_type}
                    onChange={(e) => setNewItem(i, { entry_type: e.target.value })}
                  />
                  {newItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNewItem(i)}
                      className="shrink-0 text-sm text-fg-3 hover:text-off-strong dark:text-fg-3 px-1"
                      aria-label="Remove item"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className={`${inputCls} min-w-0 flex-1`}
                    value={it.category_slug}
                    onChange={(e) => setNewItem(i, { category_slug: e.target.value })}
                    aria-label="Category"
                  >
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                  {/* Duration is the number that actually matters for a workout — without
                      it a saved run/session logs as a bare name with nothing to measure. */}
                  {kind.duration && (
                    <input
                      className={`${inputCls} w-16 shrink-0`}
                      placeholder="mins"
                      inputMode="decimal"
                      value={it.duration_minutes ?? ''}
                      onChange={(e) => setNewItem(i, { duration_minutes: e.target.value })}
                      aria-label="Minutes"
                    />
                  )}
                  <input
                    className={`${inputCls} w-16 shrink-0`}
                    placeholder={kind.duration ? 'reps' : 'amt'}
                    inputMode="decimal"
                    value={it.value}
                    onChange={(e) => setNewItem(i, { value: e.target.value })}
                    aria-label={kind.duration ? 'Reps' : 'Amount'}
                  />
                  <input
                    className={`${inputCls} w-16 shrink-0`}
                    placeholder="unit"
                    value={it.value_unit}
                    onChange={(e) => setNewItem(i, { value_unit: e.target.value })}
                    aria-label="Unit"
                  />
                </div>
              </div>
              )
            ))}
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={addNewItem}
                className="text-sm font-medium text-fg-2 hover:underline"
              >
                + Add item
              </button>
              {kind.key === 'supplements' && !newItems.some((it) => it.ref === 'regimen') && (
                <button
                  type="button"
                  onClick={addRegimenRef}
                  className="text-sm font-medium text-fg-2 hover:underline"
                >
                  + My supplement stack
                </button>
              )}
            </div>
          </div>
          </>
          )}
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={submitCreate} disabled={busy || !kind}>
              Save routine
            </Button>
            <Button size="sm" variant="ghost" onClick={resetCreate} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Suggested for you — patterns mined from what keeps getting logged by
          hand. Two-tier by design: nothing here joins "Your routines" without
          the Add tap, and "No thanks" is remembered forever. */}
      {suggestions.length > 0 && (
        <div className="rounded-lg border border-fg-3  dark:border-fg-3/40 dark: p-4 space-y-3">
          <div>
            <h3 className="text-base font-medium text-fg">
              Suggested for you
            </h3>
            <p className="text-sm text-fg-2 ">
              Noticed in what you've been logging — add one to make it a one-tap routine.
            </p>
          </div>
          {suggestions.map((s) => (
            // Stacked, not name-vs-buttons in one row: on a phone that squeezed
            // the name (the one thing being offered) down to "Cycl…".
            <div key={s.id} className="space-y-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {s.icon && <span className="shrink-0" aria-hidden>{s.icon}</span>}
                  <span className="text-base font-medium text-fg truncate">
                    {s.name}
                  </span>
                </div>
                <span className="block text-sm text-fg-2">
                  {s.items.length} {s.items.length === 1 ? 'item' : 'items'}
                  {s.seen_days ? ` · logged ${s.seen_days} times lately` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" onClick={() => acceptSuggestion(s)}>Add</Button>
                <button
                  type="button"
                  onClick={() => dismissSuggestion(s)}
                  className="text-sm text-fg-2 underline"
                >
                  No thanks
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template list */}
      {loading ? (
        <p className="text-sm text-fg-3">Loading…</p>
      ) : templates.length === 0 && !creating ? (
        <p className="text-sm text-fg-3">
          No routines yet. Make one for a workout or stack you log often.
        </p>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div
              key={t.id}
              className="rounded-lg border border-line"
            >
              <div className="flex items-center justify-between gap-2 p-3">
                {/* The name gets the whole line and the count sits under it — inline, the
                    three action controls squeezed a phone-width name down to "Work…", and
                    the name is exactly what you have to say out loud to log it. */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {t.icon && <span className="shrink-0" aria-hidden>{t.icon}</span>}
                    <span className="text-base font-medium text-fg truncate">
                      {t.name}
                    </span>
                  </div>
                  <span className="block text-sm text-fg-3">
                    {t.items.length} {t.items.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => togglePin(t)}
                    aria-pressed={pinnedIds.has(t.id)}
                    title={pinnedIds.has(t.id)
                      ? 'Unpin from today’s checklist'
                      : 'Pin to today’s checklist for one-tap logging'}
                    className={`text-lg leading-none px-1 ${
                      pinnedIds.has(t.id)
                        ? 'text-fg-2'
                        : 'text-fg-3 hover:text-fg '
                    }`}
                  >
                    {pinnedIds.has(t.id) ? '★' : '☆'}
                  </button>
                  {applyingId === t.id ? (
                    <Button size="sm" variant="ghost" onClick={() => setApplyingId(null)}>Close</Button>
                  ) : (
                    <Button size="sm" onClick={() => openApply(t)}>Log</Button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t)}
                    className="text-sm text-fg-3 hover:text-off-strong px-1"
                    aria-label={`Delete ${t.name}`}
                  >
                    🗑
                  </button>
                </div>
              </div>

              {/* Adjustable apply panel */}
              {applyingId === t.id && (
                <div className="border-t border-line p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <label className={labelCls}>Date</label>
                    <input
                      type="date"
                      className={`${inputCls} w-auto`}
                      value={applyDate}
                      onChange={(e) => setApplyDate(e.target.value)}
                    />
                  </div>
                  <ul className="space-y-2">
                    {rows.map((r, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={r.include}
                          onChange={() => toggleRow(i)}
                          className="h-4 w-4 accent-fg"
                          aria-label={`Include ${itemLabel(r)}`}
                        />
                        <span
                          className={`text-sm capitalize flex-1 ${
                            r.include
                              ? 'text-fg'
                              : 'text-fg-3 line-through'
                          }`}
                        >
                          {itemLabel(r)}
                        </span>
                        <input
                          className={`${inputCls} w-20`}
                          inputMode="decimal"
                          value={r.value}
                          disabled={!r.include}
                          onChange={(e) => editRowValue(i, e.target.value)}
                          aria-label={`Amount for ${itemLabel(r)}`}
                        />
                        <span className="text-sm text-fg-3 w-12">
                          {r.value_unit || ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" onClick={() => submitApply(t)} disabled={busy}>
                    {busy ? 'Logging…' : `Log ${rows.filter((r) => r.include).length} items`}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoutineManager;
